import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { FilePondFile } from 'filepond';
import type IntlService from 'ember-intl/services/intl';
import type StoreService from 'scaleflow/services/store';
import { initializeVdrClient } from 'scaleflow/utils/aws-clients/s3VDRClient';
import type { S3ObjectSummary } from 'scaleflow/utils/aws-clients/s3VDRClient';
import {
  libraryKey,
  libraryPrefix,
  type UploadProgressEntry,
} from 'scaleflow/utils/manual-repo-archives';

type VdrClient = Awaited<ReturnType<typeof initializeVdrClient>>;

export default class ConnectorsManualController extends Controller {
  @service declare store: StoreService;
  @service declare organisation: any;
  @service declare intl: IntlService;
  @service declare notifications: any;

  @tracked archives: S3ObjectSummary[] = [];
  @tracked loadFailed = false;
  @tracked isRefreshing = false;

  /** Archives awaiting delete confirmation; empty when nothing is pending. */
  @tracked pendingDelete: S3ObjectSummary[] = [];
  /**
   * Live byte counts for the batch currently uploading, fed straight to the
   * uploader. Reassigned rather than mutated on each event — a tracked array's
   * elements are not themselves tracked.
   */
  @tracked uploadProgress: UploadProgressEntry[] = [];
  @tracked deletingKeys: string[] = [];
  @tracked errorMessage: string | null = null;

  /**
   * Feeds `Scans::RepositoryUploads`'s overwrite warning. Compared
   * case-sensitively there, because S3 keys are: `API.tar.gz` and `api.tar.gz`
   * are genuinely different objects.
   */
  get existingFilenames(): string[] {
    return this.archives.map((archive) => archive.name);
  }

  private get tenantId(): string {
    return this.organisation.id as string;
  }

  /** Extracted so tests can override it on the instance rather than hit AWS. */
  private async getVdrClient(): Promise<VdrClient> {
    return initializeVdrClient(this.store);
  }

  private async reload(client?: VdrClient) {
    const vdr = client ?? (await this.getVdrClient());
    this.archives = await vdr.listObjects(libraryPrefix(this.tenantId));
    this.loadFailed = false;
  }

  @action
  async refresh() {
    if (this.isRefreshing) return;
    this.isRefreshing = true;
    try {
      await this.reload();
    } catch (error) {
      console.error('Failed to refresh manually uploaded source code', error);
      this.errorMessage = this.intl.t('manual-uploads.load-failed');
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Uploads each staged archive into the tenant's library.
   *
   * Uploads run in parallel but the listing is refreshed once at the end
   * rather than per file — the list is a view of S3, and re-reading it N times
   * for N uploads would only produce N partial pictures.
   */
  @action
  async uploadArchives(pondFiles: FilePondFile[]) {
    if (!pondFiles.length) return;
    this.errorMessage = null;
    this.isRefreshing = true;

    const files = pondFiles.map((pondFile) => pondFile.file as File);
    // Seeded up front so every file has a bar from the first paint, rather than
    // rows appearing one by one as their first progress event lands.
    this.uploadProgress = files.map((file) => ({
      filename: file.name,
      loaded: 0,
      total: file.size,
    }));

    const client = await this.getVdrClient();
    const failed: string[] = [];

    await Promise.all(
      files.map(async (file) => {
        try {
          await client.uploadFileMultipart(
            {
              key: libraryKey(this.tenantId, file.name),
              body: file,
              contentType: file.type || 'application/gzip',
            },
            (loaded, total) => this.recordProgress(file, loaded, total),
          );
        } catch (error) {
          console.error(`Failed to upload "${file.name}"`, error);
          failed.push(file.name);
        }
      }),
    );

    this.uploadProgress = [];

    try {
      await this.reload(client);
    } catch (error) {
      console.error('Uploaded, but failed to refresh the listing', error);
    } finally {
      this.isRefreshing = false;
    }

    if (failed.length) {
      this.errorMessage = this.intl.t('manual-uploads.add-failed', {
        name: failed.join(', '),
      });
    }
  }

  /**
   * `total` comes from the SDK's progress event, which can omit it; the file's
   * own size is the reliable denominator, so it is only a fallback.
   */
  private recordProgress(file: File, loaded: number, total?: number) {
    this.uploadProgress = this.uploadProgress.map((entry) =>
      entry.filename === file.name
        ? { ...entry, loaded, total: entry.total || total || 0 }
        : entry,
    );
  }

  @action
  requestDelete(archives: S3ObjectSummary[]) {
    this.pendingDelete = archives;
  }

  @action
  cancelDelete() {
    this.pendingDelete = [];
  }

  /**
   * Rows stay listed, each with its spinner, until S3 confirms the delete:
   * hiding them first would claim they're gone while the requests are still
   * running, and a refresh in that window would bring them straight back.
   * Safe without any reference-checking: no repository entry ever points at
   * a library object, so deleting one cannot affect an X-Ray that used it.
   */
  @action
  async confirmDelete() {
    const archives = this.pendingDelete;
    if (!archives.length || this.deletingKeys.length) return;

    this.deletingKeys = archives.map((archive) => archive.key);
    this.pendingDelete = [];
    this.errorMessage = null;

    try {
      const client = await this.getVdrClient();
      // Settled rather than all-or-nothing: one refusal must not hide that the
      // rest really are gone.
      const results = await Promise.allSettled(
        archives.map((archive) => client.deleteObject(archive.key)),
      );
      const deleted = new Set<string>();
      archives.forEach((archive, i) => {
        const result = results[i]!;
        if (result.status === 'fulfilled') deleted.add(archive.key);
        else console.error(`Failed to delete "${archive.name}"`, result.reason);
      });

      this.archives = this.archives.filter((a) => !deleted.has(a.key));
      if (deleted.size < archives.length) {
        this.errorMessage = this.intl.t('manual-uploads.delete-failed');
      }
      if (deleted.size) {
        this.notifications?.toggleShowNotification?.(
          this.intl.t('manual-uploads.deleted', { count: deleted.size }),
        );
      }
    } catch (error) {
      console.error('Failed to delete archives', error);
      this.errorMessage = this.intl.t('manual-uploads.delete-failed');
    } finally {
      this.deletingKeys = [];
    }
  }
}
